/**
 * LoadingState — skeleton variants for table, card, and form layouts
 *
 * @example
 * if (loading) return <LoadingState variant="table" rows={5} />;
 * if (loading) return <LoadingState variant="cards" count={6} />;
 * if (loading) return <LoadingState variant="form" fields={4} />;
 */

import PropTypes from 'prop-types';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

// ─── Table skeleton ───────────────────────────────────────────────────────────
function TableSkeleton({ rows, cols }) {
  return (
    <div className="rounded-xl border-2 border-[var(--border-light)] overflow-hidden bg-white">
      {/* Header */}
      <div className="flex gap-4 px-4 py-3 bg-[var(--bg-app)] border-b border-[var(--border-light)]">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-4 rounded flex-1" style={{ maxWidth: i === 0 ? '60px' : undefined }} />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex gap-4 px-4 py-3 border-b border-[var(--border-light)] last:border-0"
        >
          {Array.from({ length: cols }).map((_, j) => (
            <Skeleton
              key={j}
              className="h-4 rounded flex-1"
              style={{ opacity: 1 - i * 0.08, width: `${60 + ((j * 13 + i * 7) % 30)}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Card skeleton ────────────────────────────────────────────────────────────
function CardSkeleton({ count }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="bg-white rounded-2xl border-2 border-[var(--border-light)] p-4 space-y-3"
        >
          <Skeleton className="h-32 rounded-xl w-full" />
          <Skeleton className="h-5 rounded w-3/4" />
          <Skeleton className="h-4 rounded w-1/2" />
          <div className="flex gap-2 pt-1">
            <Skeleton className="h-8 rounded-lg flex-1" />
            <Skeleton className="h-8 w-8 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Form skeleton ────────────────────────────────────────────────────────────
function FormSkeleton({ fields }) {
  return (
    <div className="bg-white rounded-2xl border-2 border-[var(--border-light)] p-5 space-y-4">
      <Skeleton className="h-5 rounded w-40 mb-5" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {Array.from({ length: fields }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-3.5 rounded w-1/3" />
            <Skeleton className="h-9 rounded-xl w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Bars skeleton ────────────────────────────────────────────────────────────
// Kullanım: yatay bar-listesi görselleştirmeleri (örn. AdminAppVersion sürüm
// dağılımı) için — TableSkeleton'daki genişlik/opacity deseniyle tutarlı, ama
// satır başına tek bir "bar" şekli (label + sayı yerine) gösterir.
const _BAR_SKELETON_WIDTHS = [88, 62, 74, 45, 90, 55];

function BarsSkeleton({ count }) {
  return (
    <div className="space-y-2.5">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-3.5 rounded w-16 flex-shrink-0" style={{ opacity: 1 - i * 0.08 }} />
          <Skeleton
            className="h-3.5 rounded-full flex-1"
            style={{
              opacity: 1 - i * 0.08,
              maxWidth: `${_BAR_SKELETON_WIDTHS[i % _BAR_SKELETON_WIDTHS.length]}%`,
            }}
          />
        </div>
      ))}
    </div>
  );
}

// ─── LoadingState ─────────────────────────────────────────────────────────────
export default function LoadingState({ variant, rows, cols, count, fields, className }) {
  return (
    <div
      className={cn('w-full', className)}
      role="status"
      aria-busy="true"
      aria-label="Loading"
    >
      {variant === 'table' && <TableSkeleton rows={rows} cols={cols} />}
      {variant === 'cards' && <CardSkeleton count={count} />}
      {variant === 'form' && <FormSkeleton fields={fields} />}
      {variant === 'bars' && <BarsSkeleton count={count} />}
    </div>
  );
}

LoadingState.propTypes = {
  /** Layout variant */
  variant: PropTypes.oneOf(['table', 'cards', 'form', 'bars']),
  /** Number of table rows */
  rows: PropTypes.number,
  /** Number of table columns */
  cols: PropTypes.number,
  /** Number of cards */
  count: PropTypes.number,
  /** Number of form fields */
  fields: PropTypes.number,
  className: PropTypes.string,
};

LoadingState.defaultProps = {
  variant: 'table',
  rows: 5,
  cols: 4,
  count: 6,
  fields: 4,
};
